<template>
  <v-container class="region-detail mx-2" fluid>
    <v-breadcrumbs class="px-0 pt-0 pb-2" :items="breadcrumbs" />

    <div class="region-detail__header">
      <div class="region-detail__header-left">
        <v-icon color="primary">mdi-city-variant-outline</v-icon>
        <h1 class="region-detail__title">{{ regionName }}</h1>
      </div>
      <v-btn
        color="primary"
        outlined
        rounded
        class="text-none"
        :to="{ path: '/areas', query: { tab: 'regions' } }"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        К списку
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="!loading && region">
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="4">
          <v-card flat outlined class="region-stat pa-4">
            <div class="region-stat__value">{{ doorphonesTotalDisplay }}</div>
            <div class="region-stat__label">Домофонов в районе</div>
            <div v-if="!canLoadDoorphones" class="region-stat__hint">Нет доступа к статистике</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-card flat outlined class="region-stat pa-4">
            <div class="region-stat__value">{{ cityName }}</div>
            <div class="region-stat__label">Город</div>
          </v-card>
        </v-col>
      </v-row>

      <div class="region-detail__tabs-wrap">
        <v-tabs v-model="activeTab" class="region-detail__tabs" grow show-arrows="mobile">
          <v-tab v-for="tab in tabs" :key="tab.key" class="region-detail__tab">
            <v-icon v-if="tab.icon" left small>{{ tab.icon }}</v-icon>
            {{ tab.name }}
          </v-tab>
        </v-tabs>
      </div>

      <v-tabs-items v-model="activeTab" class="region-detail__tabs-items">
        <v-tab-item>
          <v-card flat outlined class="region-detail__card region-detail__overview pa-5 pa-md-6">
            <template v-if="regionInfo">
              <p class="region-detail__summary mb-5">
                {{ regionInfo.summary }}
              </p>

              <v-row class="mb-5">
                <v-col cols="12" sm="4">
                  <div class="region-detail__fact">
                    <v-icon color="primary" class="region-detail__fact-icon">mdi-account-group</v-icon>
                    <div class="region-detail__fact-label">Население</div>
                    <div class="region-detail__fact-value">{{ regionInfo.population }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="region-detail__fact">
                    <v-icon color="primary" class="region-detail__fact-icon">mdi-vector-square</v-icon>
                    <div class="region-detail__fact-label">Площадь</div>
                    <div class="region-detail__fact-value">{{ regionInfo.areaSize }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="4">
                  <div class="region-detail__fact">
                    <v-icon color="primary" class="region-detail__fact-icon">mdi-calendar</v-icon>
                    <div class="region-detail__fact-label">Образован</div>
                    <div class="region-detail__fact-value">{{ regionInfo.formed }} г.</div>
                  </div>
                </v-col>
              </v-row>

              <div class="region-detail__location mb-5">
                <v-icon color="primary" class="mr-2">mdi-map-marker-radius</v-icon>
                <span>{{ regionInfo.location }}</span>
              </div>

              <h3 class="region-detail__section-title">О районе</h3>
              <p class="region-detail__text mb-5">{{ regionInfo.description }}</p>

              <h3 class="region-detail__section-title">Особенности</h3>
              <div class="region-detail__chips mb-5">
                <v-chip
                  v-for="(item, index) in regionInfo.highlights"
                  :key="index"
                  class="ma-1"
                  outlined
                  color="primary"
                >
                  {{ item }}
                </v-chip>
              </div>
            </template>

            <v-alert v-else type="info" outlined class="mb-5 region-detail__alert">
              Справочное описание для этого района пока не добавлено.
            </v-alert>

            <v-divider class="my-5" />

            <h3 class="region-detail__section-title">В системе домофонии</h3>
            <p class="region-detail__text mb-2">
              Район «{{ region.name }}» используется при привязке адресов домофонов и другого оборудования
              в городе {{ cityName }}.
            </p>
            <p class="region-detail__system-note mb-0">
              Привязанных домофонов: <strong>{{ doorphonesTotalDisplay }}</strong>.
            </p>

            <v-alert
              v-if="canLoadDoorphones && doorphonesTotal === 0"
              type="info"
              outlined
              class="mt-4 mb-0 region-detail__alert"
            >
              В этом районе пока нет привязанных домофонов.
            </v-alert>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat outlined class="region-detail__card pa-4">
            <v-row align="center" class="mb-2 areas-toolbar">
              <div class="areas-toolbar__search ml-3">
                <BaseTextField
                  v-model="doorphoneSearch"
                  type-input="string"
                  :is-button="false"
                  label="Адрес"
                />
              </div>
              <v-spacer />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="#4C6C93"
                    class="border card-footer mr-4"
                    v-bind="attrs"
                    v-on="on"
                    :loading="doorphonesLoading"
                    :disabled="!canLoadDoorphones"
                    @click="loadDoorphones"
                  >
                    <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
                  </v-btn>
                </template>
                <span>Обновить</span>
              </v-tooltip>
            </v-row>

            <v-alert v-if="!canLoadDoorphones" type="warning" dense outlined class="mb-4">
              Список домофонов доступен пользователям с правом «Массовые функции».
            </v-alert>

            <v-data-table
              v-else
              class="table"
              :headers="doorphoneHeaders"
              :items="doorphones"
              :loading="doorphonesLoading"
              :page.sync="doorphonePage"
              :items-per-page.sync="doorphoneItemsPerPage"
              :server-items-length="doorphonesTotal || 0"
              :footer-props="footerProps"
              item-key="id"
            >
              <template v-slot:item.address="{ item }">
                <router-link :to="`/doorphones/${item.id}`" class="routerLink">
                  {{ item.address }}
                </router-link>
              </template>
              <template v-slot:item.physical_status="{ item }">
                {{ physicalStatusLabel(item.physical_status) }}
              </template>
              <template v-slot:item.map="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      text
                      small
                      v-bind="attrs"
                      v-on="on"
                      color="#1E6CEB"
                      class="text-none"
                      :to="`/map/none/doorphone/${item.id}`"
                    >
                      <v-icon color="green">mdi-map-marker-right-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Домофон на карте</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item class="region-detail__tab-territories">
          <v-card
            flat
            outlined
            class="region-detail__card region-detail__card--territories"
          >
            <region-territories-map
              v-if="regionId"
              :region-id="regionId"
              :region-name="regionName"
              :active="activeTab === 2"
              :markers="skudVisible ? skudMapMarkers : []"
            >
              <template v-if="canShowSkud" #controls>
                <v-btn
                  color="primary"
                  outlined
                  rounded
                  class="text-none region-detail__skud-btn"
                  :loading="skudLoading"
                  @click="toggleSkud"
                >
                  <v-icon left small>mdi-shield-key-outline</v-icon>
                  {{ skudVisible ? 'Скрыть СКУД района' : 'Показать СКУД района' }}
                </v-btn>
              </template>
            </region-territories-map>
          </v-card>
        </v-tab-item>

        <v-tab-item>
          <v-card flat outlined class="region-detail__card pa-6">
            <region-section-placeholder
              title="Новости и акции района"
              description="Публикация новостей и акций для района в разработке..."
              icon="mdi-newspaper-variant-outline"
            />
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <v-card v-else-if="!loading && !region" flat outlined class="pa-6 text-center grey--text">
      Район не найден или не указан.
      <div class="mt-3">
        <v-btn color="primary" text :to="{ path: '/areas', query: { tab: 'regions' } }">
          Вернуться к списку
        </v-btn>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseTextField from '@/components/BaseTextField';
import RegionSectionPlaceholder from '@/components/areas/RegionSectionPlaceholder';
import RegionTerritoriesMap from '@/components/areas/RegionTerritoriesMap';
import RegionApi from '@/services/api/region';
import { getRegionDescription } from '@/components/areas/regionDescriptions';

const CITY_NAMES = {
  1: 'Ульяновск',
  2: 'Саранск',
};

const PHYSICAL_STATUSES = {
  1: 'Установлен',
  2: 'Не установлен',
  3: 'Без интернет соединения',
  4: 'Пусконаладка',
  5: 'Старый домофон',
  6: 'Ожидает монтажа',
};

const TAB_DOORPHONES = 1;
const TAB_TERRITORIES = 2;

export default {
  name: 'AreaDescription',
  components: { BaseTextField, RegionSectionPlaceholder, RegionTerritoriesMap },
  data () {
    return {
      loading: false,
      activeTab: 0,
      region: null,
      doorphones: [],
      doorphonesTotal: null,
      doorphonesLoading: false,
      doorphoneSearch: '',
      searchTimer: null,
      doorphonePage: 1,
      doorphoneItemsPerPage: 20,
      skudVisible: false,
      skudLoading: false,
      skudEquipment: [],
      footerProps: {
        showFirstLastPage: true,
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': 'Строк на странице',
      },
      doorphoneHeaders: [
        { text: 'ID', value: 'id', width: '8%' },
        { text: 'Адрес', value: 'address', sortable: false },
        { text: 'Агент', value: 'agent', sortable: false },
        { text: 'Диапазон', value: 'diapason', sortable: false, width: '10%' },
        { text: 'Статус', value: 'physical_status', sortable: false, width: '14%' },
        { text: 'На карте', value: 'map', sortable: false, width: '8%', align: 'center' },
      ],
      tabs: [
        { key: 'overview', name: 'Обзор', icon: 'mdi-view-dashboard-outline' },
        { key: 'doorphones', name: 'Домофоны', icon: 'mdi-door' },
        { key: 'territories', name: 'Территории', icon: 'mdi-map-marker-path' },
        { key: 'news', name: 'Новости', icon: 'mdi-newspaper-variant-outline' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      roleName: 'roleName',
    }),
    regionId () {
      const paramId = this.$route.params.id;
      const queryId = this.$route.query.id;
      const raw = paramId || queryId;
      return raw ? Number(raw) : null;
    },
    regionName () {
      return (this.region && this.region.name) || 'Район';
    },
    cityName () {
      if (!this.region || !this.region.city_id) return '—';
      return CITY_NAMES[this.region.city_id] || '—';
    },
    regionInfo () {
      if (!this.regionId) return null;
      return getRegionDescription(this.regionId);
    },
    doorphonesTotalDisplay () {
      return this.doorphonesTotal !== null && this.doorphonesTotal !== undefined
        ? this.doorphonesTotal
        : '—';
    },
    canLoadDoorphones () {
      return this.$checkPermission('get_mf_doorphones');
    },
    canShowSkud () {
      return this.roleName === 'admin' && this.$checkPermission('get_region_skud_equipment');
    },
    breadcrumbs () {
      return [
        { text: 'Районы', disabled: false, href: '/areas?tab=regions' },
        { text: this.regionName, disabled: true },
      ];
    },
    skudMapMarkers () {
      return this.skudEquipment.filter((item) => {
        const lat = Number(item.geo_lat);
        const lon = Number(item.geo_lon);
        return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
      });
    },
  },
  watch: {
    regionId: {
      immediate: true,
      handler () {
        this.loadRegion();
      },
    },
    doorphonePage () {
      this.loadDoorphones();
    },
    doorphoneItemsPerPage () {
      this.doorphonePage = 1;
      this.loadDoorphones();
    },
    doorphoneSearch () {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.doorphonePage = 1;
        if (this.activeTab === TAB_DOORPHONES && this.canLoadDoorphones) {
          this.loadDoorphones();
        }
      }, 400);
    },
    activeTab (index) {
      if (index === TAB_DOORPHONES && this.canLoadDoorphones && !this.doorphones.length && this.region) {
        this.loadDoorphones();
      }
      if (index !== TAB_TERRITORIES) {
        this.skudVisible = false;
      }
    },
  },
  methods: {
    ...mapActions({
      fetchRegion: 'Region/getRegion',
      getMfDoorphones: 'Doorphone/getMfDoorphones',
    }),
    async loadRegion () {
      if (!this.regionId) {
        this.region = null;
        return;
      }

      this.loading = true;
      this.region = null;
      this.doorphones = [];
      this.doorphonesTotal = null;
      this.skudVisible = false;
      this.skudEquipment = [];

      try {
        this.region = await this.fetchRegion(this.regionId);
        await this.loadDoorphonesCount();
      } catch (e) {
        this.$flash_warning('Не удалось загрузить район');
      } finally {
        this.loading = false;
      }
    },
    async loadDoorphonesCount () {
      if (!this.canLoadDoorphones || !this.regionId) return;

      try {
        const response = await this.getMfDoorphones({
          page: 1,
          limit: 1,
          filters: { region_id: this.regionId },
        });
        this.doorphonesTotal = response.meta && response.meta.total != null
          ? response.meta.total
          : 0;
      } catch (e) {
        this.doorphonesTotal = null;
      }
    },
    async loadDoorphones () {
      if (!this.canLoadDoorphones || !this.regionId) return;

      this.doorphonesLoading = true;
      const filters = { region_id: this.regionId };
      if (this.doorphoneSearch) {
        filters.address = this.doorphoneSearch;
      }

      try {
        const response = await this.getMfDoorphones({
          page: this.doorphonePage,
          limit: this.doorphoneItemsPerPage,
          filters,
        });
        this.doorphones = response.doorphones || [];
        this.doorphonesTotal = response.meta && response.meta.total != null
          ? response.meta.total
          : this.doorphones.length;
      } catch (e) {
        this.$flash_warning('Не удалось загрузить домофоны района');
      } finally {
        this.doorphonesLoading = false;
      }
    },
    physicalStatusLabel (status) {
      return PHYSICAL_STATUSES[status] || '—';
    },
    toggleSkud () {
      if (this.skudVisible) {
        this.skudVisible = false;
        this.skudEquipment = [];
        return;
      }

      this.skudVisible = true;
      this.loadSkudEquipment();
    },
    async loadSkudEquipment () {
      if (!this.regionId) return;

      this.skudLoading = true;
      try {
        const { data } = await RegionApi.getRegionSkudEquipment(this.regionId);
        const payload = data.data || {};
        this.skudEquipment = [
          ...(payload.doorphones || []),
          ...(payload.gates || []),
          ...(payload.barriers || []),
          ...(payload.additional_entrances || []),
        ];
      } catch (e) {
        this.skudEquipment = [];
        this.skudVisible = false;
        this.$flash_warning('Не удалось загрузить оборудование СКУД');
      } finally {
        this.skudLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './areasList.scss';

.region-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.region-detail__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.region-detail__title {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
}

.region-detail__tabs-wrap {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px 12px 0 0;
  border-bottom: none;
  overflow: hidden;
}

.region-detail__tabs ::v-deep .v-tabs-bar {
  background: transparent;
  height: 48px;
}

.region-detail__tab {
  text-transform: none;
  font-size: 0.875rem;
  font-weight: 500;
  min-height: 48px;
}

.region-detail__tabs-items {
  background: transparent;
}

.region-detail__card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-top: none;
}

.region-detail__tab-territories {
  overflow: hidden;
}

.region-detail__card--territories {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 360px);
  min-height: 360px;
  max-height: calc(100vh - 360px);
}

.region-detail__card--territories > .region-territories {
  flex: 1 1 auto;
  min-height: 0;
}

.region-detail__skud-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.region-stat {
  text-align: center;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__value {
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1.2;
  }

  &__label {
    font-size: 0.9375rem;
    color: #78909c;
    margin-top: 4px;
  }

  &__hint {
    font-size: 0.875rem;
    color: #b0bec5;
    margin-top: 6px;
  }
}

.region-detail__overview {
  font-size: 1rem;
}

.region-detail__summary {
  font-size: 1.125rem;
  line-height: 1.6;
  color: #263238;
  font-weight: 500;
}

.region-detail__section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #263238;
  margin: 0 0 12px;
  line-height: 1.4;
}

.region-detail__text {
  font-size: 1rem;
  line-height: 1.65;
  color: #37474f;
}

.region-detail__system-note {
  font-size: 1rem;
  line-height: 1.5;
  color: #546e7a;
}

.region-detail__location {
  display: flex;
  align-items: flex-start;
  font-size: 1rem;
  line-height: 1.55;
  color: #455a64;
  padding: 14px 16px;
  background: #f5f8fa;
  border-radius: 8px;
}

.region-detail__fact {
  padding: 12px 0;
}

.region-detail__fact-icon {
  font-size: 28px !important;
  margin-bottom: 6px;
}

.region-detail__fact-label {
  font-size: 0.9375rem;
  color: #78909c;
  margin-bottom: 2px;
}

.region-detail__fact-value {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #263238;
  line-height: 1.3;
}

.region-detail__chips ::v-deep .v-chip {
  font-size: 0.9375rem;
  height: 32px;
}

.region-detail__alert {
  font-size: 1rem;
}

::v-deep a.routerLink {
  color: inherit;
  text-decoration: none;
}

::v-deep a.routerLink:hover {
  text-decoration: underline;
}
</style>
